import { LIVE_NPC_CONFIG, isNpcMessageText, type LiveNpcSnapshot, type ServerNpcConversation, type ServerNpcMessage } from "@endclock/shared";
import type { NetworkClient } from "../game/network.js";

export function npcAvailabilityLabel(npc: LiveNpcSnapshot, distance: number): string {
  if (npc.availability === "UNAVAILABLE") return "UNAVAILABLE";
  if (npc.availability === "BUSY") return `IN CONVERSATION · ${Math.ceil(npc.activeRemainingMs / 1_000)}s`;
  if (npc.availability === "COOLDOWN") return `WAIT · ${Math.ceil(npc.cooldownRemainingMs / 1_000)}s`;
  if (npc.availability === "OTHER_CONVERSATION") return "FINISH CURRENT CONVERSATION";
  return distance > LIVE_NPC_CONFIG.conversationRange ? "MOVE CLOSER" : "TALK";
}

export function activeBudgetRemaining(conversation: ServerNpcConversation, receivedAt: number, now: number): number {
  return Math.max(0, conversation.activeRemainingMs - (conversation.state === "ACTIVE" ? Math.max(0, now - receivedAt) : 0));
}

export class NpcConversationPanel {
  readonly element = document.createElement("aside");
  private readonly title = document.createElement("strong");
  private readonly timer = document.createElement("span");
  private readonly counter = document.createElement("span");
  private readonly transcript = document.createElement("div");
  private readonly status = document.createElement("p");
  private readonly input = document.createElement("input");
  private readonly send = document.createElement("button");
  private readonly closeButton = document.createElement("button");
  private readonly replyBubbles = new Map<string, { element: HTMLElement; text: Text }>();
  private readonly accepted = new Set<string>();
  private conversation: ServerNpcConversation | null = null;
  private receivedAt = 0;
  private pending: string | null = null;
  private notice = "";
  private sequence = 0;
  private feedbackUntil = 0;

  constructor(parent: HTMLElement, private readonly network: Pick<NetworkClient, "sendNpcCommand" | "state">) {
    this.element.className = "npc-conversation";
    this.element.id = "npc-conversation";
    this.element.hidden = true;
    this.element.setAttribute("aria-label", "Private NPC conversation");
    const header = document.createElement("header");
    this.title.id = "npc-conversation-name";
    this.closeButton.type = "button";
    this.closeButton.textContent = "CLOSE";
    this.closeButton.addEventListener("click", () => this.close());
    header.append(this.title, this.closeButton);
    const limits = document.createElement("div");
    limits.className = "npc-conversation-limits";
    this.timer.id = "npc-conversation-timer";
    this.counter.id = "npc-conversation-counter";
    limits.append(this.timer, this.counter);
    this.transcript.className = "npc-transcript";
    this.transcript.id = "npc-transcript";
    this.transcript.setAttribute("role", "log");
    this.transcript.setAttribute("aria-live", "polite");
    this.transcript.setAttribute("aria-relevant", "additions text");
    this.status.id = "npc-conversation-status";
    this.status.setAttribute("role", "status");
    const form = document.createElement("form");
    this.input.id = "npc-message-input";
    this.input.type = "text";
    this.input.autocomplete = "off";
    this.input.maxLength = LIVE_NPC_CONFIG.maximumMessageChars;
    this.input.placeholder = "Say something…";
    this.input.setAttribute("aria-label", "Message to NPC");
    this.input.addEventListener("input", () => this.update());
    this.send.type = "submit";
    this.send.textContent = "SEND";
    form.append(this.input, this.send);
    form.addEventListener("submit", (event) => { event.preventDefault(); this.submit(); });
    this.element.addEventListener("keydown", (event) => { if (event.key === "Escape") { event.stopPropagation(); this.close(); } });
    this.element.append(header, limits, this.transcript, this.status, form);
    parent.append(this.element);
  }

  handle(message: ServerNpcMessage): void {
    if (message.t === "npc_conversation") {
      const fresh = this.conversation?.encounterId !== message.encounterId;
      if (fresh) {
        this.transcript.replaceChildren();
        this.replyBubbles.clear();
        this.accepted.clear();
        this.pending = null;
        this.input.value = "";
        this.notice = "";
        this.element.hidden = false;
      }
      this.conversation = message;
      this.receivedAt = performance.now();
      this.title.textContent = message.displayName;
      this.element.dataset.state = message.state;
      if (message.state === "CLOSED") {
        this.pending = null;
        this.notice = endReasonLabel(message.endReason);
        for (const { element: bubble } of this.replyBubbles.values()) {
          if (bubble.dataset.streaming === "true") { bubble.dataset.streaming = "false"; bubble.dataset.incomplete = "true"; }
        }
      }
      this.update();
      if (fresh && message.state === "ACTIVE") this.input.focus({ preventScroll: true });
      return;
    }
    if (message.t === "npc_error") {
      if (message.encounterId && message.encounterId !== this.conversation?.encounterId) return;
      this.notice = errorLabel(message.code);
      this.pending = null;
      if (!this.conversation || this.element.hidden) {
        this.title.textContent = "CONVERSATION";
        this.element.hidden = false;
        this.feedbackUntil = performance.now() + 5_000;
      }
      if (message.requestId) {
        const bubble = this.replyBubbles.get(message.requestId);
        if (bubble) { bubble.element.dataset.streaming = "false"; bubble.element.dataset.incomplete = "true"; }
      }
      this.update();
      return;
    }
    if (message.encounterId !== this.conversation?.encounterId) return;
    if (message.t === "npc_player_message") {
      if (this.accepted.has(message.requestId) || this.accepted.size >= LIVE_NPC_CONFIG.maximumPlayerMessages) return;
      this.accepted.add(message.requestId);
      this.appendBubble("player", message.text);
    } else if (message.t === "npc_reply_start") {
      this.reply(message.requestId);
    } else if (message.t === "npc_reply_delta") {
      const bubble = this.reply(message.requestId);
      if (bubble) bubble.text.appendData(message.text.slice(0, Math.max(0, LIVE_NPC_CONFIG.maximumReplyChars - bubble.text.length)));
    } else if (message.t === "npc_reply_final") {
      const bubble = this.reply(message.requestId);
      if (bubble) { bubble.text.data = message.text.slice(0, LIVE_NPC_CONFIG.maximumReplyChars); bubble.element.dataset.streaming = "false"; }
      if (this.pending === message.requestId) this.pending = null;
      this.notice = "";
    }
    this.transcript.scrollTop = this.transcript.scrollHeight;
    this.update();
  }

  update(now = performance.now()): void {
    if (this.feedbackUntil && now > this.feedbackUntil && !this.conversation) { this.element.hidden = true; this.feedbackUntil = 0; }
    const conversation = this.conversation;
    const remaining = conversation ? activeBudgetRemaining(conversation, this.receivedAt, now) : 0;
    const paused = conversation?.state === "QUEUED" || conversation?.state === "GENERATING";
    this.timer.textContent = `TIME 00:${String(Math.ceil(remaining / 1_000)).padStart(2, "0")}${paused ? " · PAUSED" : ""}`;
    this.counter.textContent = `MESSAGES ${conversation?.messagesUsed ?? 0} / ${LIVE_NPC_CONFIG.maximumPlayerMessages}`;
    const ready = conversation?.state === "ACTIVE" && remaining > 0 && !this.pending
      && conversation.messagesUsed < LIVE_NPC_CONFIG.maximumPlayerMessages && this.network.state === "online";
    this.input.disabled = !ready;
    this.send.disabled = !ready || !isNpcMessageText(this.input.value.trim());
    this.status.textContent = this.notice || (conversation?.state === "QUEUED" ? "WAITING FOR NPC · Your timer is paused."
      : conversation?.state === "GENERATING" ? "REPLYING · Your timer is paused."
      : conversation?.state === "ENDING" ? "Finishing the final reply…"
      : this.pending ? "Sending…" : "Private conversation · Trading is unavailable.");
  }

  close(): void {
    if (this.conversation && this.conversation.state !== "CLOSED") {
      this.network.sendNpcCommand({ t: "npc_close", encounterId: this.conversation.encounterId });
    }
    this.element.hidden = true;
    this.input.blur();
  }

  reset(): void {
    this.conversation = null;
    this.pending = null;
    this.replyBubbles.clear();
    this.accepted.clear();
    this.transcript.replaceChildren();
    this.element.hidden = true;
    this.feedbackUntil = 0;
    this.notice = "";
  }

  snapshot() { return { conversation: this.conversation, pending: this.pending, visible: !this.element.hidden }; }
  dispose(): void { this.element.remove(); }

  private submit(): void {
    const conversation = this.conversation;
    const text = this.input.value.trim();
    if (!conversation || this.send.disabled || this.pending || !isNpcMessageText(text)) return;
    const requestId = `npc_${Date.now().toString(36)}_${++this.sequence}`;
    this.pending = requestId;
    this.notice = "";
    this.network.sendNpcCommand({ t: "npc_message", encounterId: conversation.encounterId, requestId, text });
    this.input.value = "";
    this.update();
  }

  private reply(requestId: string): { element: HTMLElement; text: Text } | null {
    let bubble = this.replyBubbles.get(requestId);
    if (!bubble) {
      if (this.replyBubbles.size >= LIVE_NPC_CONFIG.maximumPlayerMessages) return null;
      const element = this.appendBubble("npc", "");
      const text = document.createTextNode("");
      element.append(text);
      element.dataset.requestId = requestId;
      element.dataset.streaming = "true";
      bubble = { element, text };
      this.replyBubbles.set(requestId, bubble);
    }
    return bubble;
  }

  private appendBubble(role: "npc" | "player", text: string): HTMLElement {
    const bubble = document.createElement("p");
    bubble.className = `npc-bubble npc-bubble-${role}`;
    bubble.textContent = text;
    this.transcript.append(bubble);
    return bubble;
  }
}

function errorLabel(code: string): string {
  if (code.includes("BUSY")) return "NPC IS BUSY. Try the other NPC or wait.";
  if (code.includes("COOLDOWN")) return "WAIT before speaking to this NPC again.";
  if (code.includes("RANGE")) return "MOVE CLOSER to begin a conversation.";
  if (code.includes("RATE") || code.includes("HOURLY")) return "Message limit reached. Please wait before trying again.";
  if (code.includes("OUTSTANDING") || code.includes("PENDING")) return "Wait for the current reply.";
  if (code.includes("OTHER") || code.includes("ALREADY")) return "FINISH CURRENT CONVERSATION first.";
  return "The NPC could not reply. Please try again shortly.";
}

function endReasonLabel(reason: ServerNpcConversation["endReason"]): string {
  if (reason === "MESSAGE_LIMIT") return "Conversation complete · 10 messages used.";
  if (reason === "TIME_EXPIRED") return "Conversation time ended. You can return after the cooldown.";
  if (reason === "OUT_OF_RANGE") return "Conversation ended · You moved out of range.";
  if (reason === "GENERATION_FAILED" || reason === "GENERATION_TIMEOUT") return "Reply interrupted. Please try again later.";
  return "Conversation ended. You can speak to the other NPC.";
}
